import React, { useState, useRef, useEffect } from "react";
import { createLabel } from "../../firebase/tasks";
import { getAuth } from "firebase/auth";

/**
 * TaskTitleInput Component
 *
 * A plain text input that supports @label mention autocomplete via a dropdown.
 * The overlay highlighting approach has been removed to fix cursor misalignment.
 * Labels typed as @name are highlighted in the read-only task list view instead.
 *
 * Props:
 * - value: string (the task title)
 * - onChange: (newText: string) => void
 * - onAddLabel: (labelName: string) => void
 * - allLabels: Array<{ id: string, name: string, color: string }>
 * - autoFocus: boolean
 */
export function TaskTitleInput({ value, onChange, onAddLabel, allLabels = [], autoFocus = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [cursorIdx, setCursorIdx] = useState(null);
  const [caretCoords, setCaretCoords] = useState({ top: 28, left: 0 });

  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  const matchQuery = query.toLowerCase();
  const filteredLabels = allLabels.filter(l => l.name.toLowerCase().includes(matchQuery));
  const hasExactMatch = allLabels.some(l => l.name.toLowerCase() === matchQuery);
  const showCreateOption = matchQuery.length > 0 && !hasExactMatch;
  const totalOptions = filteredLabels.length + (showCreateOption ? 1 : 0);

  const getMentionState = (text, pos) => {
    const textBefore = text.slice(0, pos);
    const lastAtIdx = textBefore.lastIndexOf("@");
    if (lastAtIdx === -1) return null;
    if (lastAtIdx > 0 && !/\s/.test(textBefore[lastAtIdx - 1])) return null;
    const wordAfterAt = textBefore.slice(lastAtIdx + 1);
    if (/\s/.test(wordAfterAt)) return null;
    return { query: wordAfterAt, startIdx: lastAtIdx };
  };

  const updateDropdown = (text, pos) => {
    const mention = getMentionState(text, pos);
    if (mention) {
      setQuery(mention.query);
      setCursorIdx(mention.startIdx);
      setSelectedIndex(0);
      setIsOpen(true);
      // Position dropdown roughly under the @ sign using char width estimate.
      // Cap left position at 120px to prevent overflowing off the right edge on mobile screens (where dropdown is ~192px wide).
      const leftOffset = mention.startIdx * 7.8;
      setCaretCoords({ top: 28, left: Math.min(leftOffset, 120) });
    } else {
      setIsOpen(false);
    }
  };

  const handleInputChange = (e) => {
    onChange(e.target.value);
    updateDropdown(e.target.value, e.target.selectionStart);
  };

  // endPos: optional override for cursor end position, used when the input may
  // have lost focus before this is called (e.g. clicking a dropdown item).
  const handleSelect = (labelName, endPos = null) => {
    if (cursorIdx === null) return;
    const text = value;
    // Prefer the supplied endPos; fall back to live selectionStart; fall back to
    // computing from the @ position + query length.
    const pos =
      endPos !== null
        ? endPos
        : inputRef.current
        ? inputRef.current.selectionStart
        : cursorIdx + 1 + query.length;
    const beforeAt = text.slice(0, cursorIdx);
    const afterCursor = text.slice(pos);
    // Remove the typed @label text entirely
    let newText = beforeAt + afterCursor;
    // Clean up any resulting double spaces
    newText = newText.replace(/\s{2,}/g, ' ');
    // If it was at the very end, trim trailing spaces
    if (afterCursor === '') {
       newText = newText.trimEnd();
    }
    
    onChange(newText);
    if (onAddLabel) onAddLabel(labelName);

    // Always ensure the label exists in task-labels collection (idempotent).
    // This is the single place that guarantees every selected label — whether
    // pre-existing on another task or brand-new — gets a task-labels document,
    // making it available in the @-mention autocomplete across all views.
    const matchedLabel = allLabels.find(l => l.name.toLowerCase() === labelName.toLowerCase());
    const labelColor = matchedLabel?.color || "#ef4444";
    const auth = getAuth();
    const userUid = auth.currentUser?.uid || "";
    createLabel(labelName.toLowerCase().trim(), labelColor, userUid).catch(err => console.error("Error creating label in TaskTitleInput:", err));

    setIsOpen(false);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        // Since we removed the text, the cursor goes where the @ was
        const newPos = cursorIdx;
        inputRef.current.setSelectionRange(newPos, newPos);
      }
    }, 0);
  };

  const handleCreate = () => {
    const newName = query.trim().replace(/\s+/g, "-").toLowerCase();
    if (!newName) return;
    // Snapshot the cursor end position NOW, before the input loses focus.
    const endPos = cursorIdx !== null ? cursorIdx + 1 + query.length : null;
    // handleSelect registers the label in task-labels (via createLabel) and
    // calls onAddLabel — no separate createLabel call needed here.
    handleSelect(newName, endPos);
  };

  const handleKeyDown = (e) => {
    if (!isOpen) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex(prev => totalOptions > 0 ? (prev + 1) % totalOptions : 0);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex(prev => totalOptions > 0 ? (prev - 1 + totalOptions) % totalOptions : 0);
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      if (showCreateOption && selectedIndex === filteredLabels.length) {
        handleCreate();
      } else if (filteredLabels[selectedIndex]) {
        handleSelect(filteredLabels[selectedIndex].name);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setIsOpen(false);
    }
  };

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        e.target !== inputRef.current
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div className="relative w-full">
      <input
        ref={inputRef}
        type="text"
        placeholder="Task name"
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onClick={(e) => updateDropdown(e.target.value, e.target.selectionStart)}
        onKeyUp={(e) => updateDropdown(e.target.value, e.target.selectionStart)}
        className="w-full border-none focus:ring-0 p-0 focus:outline-none bg-transparent text-sm font-semibold text-gray-800 placeholder-gray-300"
        autoFocus={autoFocus}
        spellCheck={false}
      />

      {/* @ Mention Dropdown */}
      {isOpen && totalOptions > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto w-48 text-sm font-normal"
          style={{ top: caretCoords.top, left: caretCoords.left }}
        >
          <ul className="py-1">
            {filteredLabels.map((l, idx) => (
              <li
                key={l.id}
                onMouseDown={(e) => { e.preventDefault(); handleSelect(l.name); }}
                className={`px-3 py-1.5 cursor-pointer flex items-center gap-2 ${
                  idx === selectedIndex ? "bg-indigo-50 text-indigo-900" : "hover:bg-gray-50"
                }`}
              >
                <span
                  className="w-2 h-2 rounded-full shrink-0"
                  style={{ backgroundColor: l.color }}
                />
                {l.name}
              </li>
            ))}
            {showCreateOption && (
              <li
                onMouseDown={(e) => { e.preventDefault(); handleCreate(); }}
                className={`px-3 py-1.5 cursor-pointer border-t border-gray-100 text-gray-600 ${
                  filteredLabels.length === selectedIndex ? "bg-indigo-50 text-indigo-900" : "hover:bg-gray-50"
                }`}
              >
                Label not found. Create <strong className="font-bold">{query}</strong>
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
